import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../lib/LanguageContext';
import translations from '../lib/translations';
import './AccountDeletion.css';

const STATUS = { IDLE: 'idle', LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AccountDeletion() {
  const [email, setEmail]       = useState('');
  const [reason, setReason]     = useState('');
  const [confirm, setConfirm]   = useState(false);
  const [status, setStatus]     = useState(STATUS.IDLE);
  const [errorMsg, setErrorMsg] = useState('');
  const { language } = useLanguage();
  const t = translations[language].deletion;

  async function handleSubmit(e) {
    e.preventDefault();

    if (!EMAIL_RE.test(email)) {
      setErrorMsg(t.errInvalidEmail);
      setStatus(STATUS.ERROR);
      return;
    }
    if (!confirm) {
      setErrorMsg(t.errNoConfirm);
      setStatus(STATUS.ERROR);
      return;
    }

    setStatus(STATUS.LOADING);
    setErrorMsg('');

    const { error } = await supabase
      .from('account_deletion_requests')
      .insert({ email: email.trim().toLowerCase(), reason: reason.trim() || null });

    if (error) {
      const isUnavailable = error.code === '42P01' || error.message?.includes('does not exist')
        || error.status === 404 || error.details?.includes('404');
      if (isUnavailable) {
        setErrorMsg(t.errUnavailable);
      } else {
        setErrorMsg(t.errGeneric);
      }
      setStatus(STATUS.ERROR);
      return;
    }

    setStatus(STATUS.SUCCESS);
  }

  if (status === STATUS.SUCCESS) {
    return (
      <div className="del-page">
        <div className="del-success">
          <span className="del-success-icon" aria-hidden="true">✓</span>
          <h1 className="del-success-title">{t.successTitle}</h1>
          <p
            className="del-success-body"
            dangerouslySetInnerHTML={{
              __html: t.successBody.replace('{email}', email),
            }}
          />
          <p className="del-success-note">
            {t.successNote}{' '}
            <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="del-page">
      {/* Header */}
      <div className="del-header">
        <p className="del-label">{t.label}</p>
        <h1 className="del-title">{t.title}</h1>
        <p className="del-intro">{t.intro}</p>
      </div>

      {/* Info box */}
      <div className="del-warn">
        <strong>{t.warnTitle}</strong>
        <ul>
          {t.warnItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
        <p className="del-warn-note">{t.warnNote}</p>
      </div>

      {/* Form */}
      <form className="del-form" onSubmit={handleSubmit} noValidate>
        <div className="del-field">
          <label htmlFor="del-email" className="del-label-field">
            {t.emailLabel} <span className="del-required" aria-hidden="true">*</span>
          </label>
          <input
            id="del-email"
            type="email"
            className="del-input"
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={(e) => { setEmail(e.target.value); setStatus(STATUS.IDLE); }}
            required
            autoComplete="email"
          />
        </div>

        <div className="del-field">
          <label htmlFor="del-reason" className="del-label-field">
            {t.reasonLabel} <span className="del-optional">{t.optional}</span>
          </label>
          <textarea
            id="del-reason"
            className="del-textarea"
            rows={4}
            placeholder={t.reasonPlaceholder}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="del-checkbox-row">
          <input
            id="del-confirm"
            type="checkbox"
            className="del-checkbox"
            checked={confirm}
            onChange={(e) => { setConfirm(e.target.checked); setStatus(STATUS.IDLE); }}
          />
          <label
            htmlFor="del-confirm"
            className="del-checkbox-label"
            dangerouslySetInnerHTML={{ __html: t.confirmLabel }}
          />
        </div>

        {status === STATUS.ERROR && (
          <p className="del-error" role="alert">{errorMsg}</p>
        )}

        <button
          type="submit"
          className="del-submit"
          disabled={status === STATUS.LOADING}
        >
          {status === STATUS.LOADING ? t.submitting : t.submitBtn}
        </button>
      </form>

      <p className="del-alt">
        {t.altText}{' '}
        <a href="mailto:admin@megablissworldwide.com?subject=Account%20Deletion%20Request">
          {t.altLink}
        </a>{' '}
        {t.altSuffix}
      </p>
    </div>
  );
}

export default AccountDeletion;
